// RamsIssueDialog — create a RAMS request (Draft) and optionally issue it
// immediately. Reuses the same "/projects/{id}/access" client-contacts picker
// pattern as the existing Issues PublishWizard.
import { useEffect, useMemo, useState } from "react";
import { X, Loader2, Send, Save, Users, Info } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const inputCls = "w-full h-9 px-3 rounded-lg border border-[#E5E0DA] bg-white text-sm focus:outline-none focus:border-[var(--brand-accent,_#C75A1B)] disabled:bg-[#F6F4F0]";

export default function RamsIssueDialog({ presetClientId, presetProjectId, directMode = false, onClose, onDone }) {
    const [clients, setClients] = useState([]);
    const [projects, setProjects] = useState([]);
    const [sites, setSites] = useState([]);
    const [buildings, setBuildings] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [staff, setStaff] = useState([]);
    const [people, setPeople] = useState([]);

    const [clientId, setClientId] = useState(presetClientId || "");
    const [projectId, setProjectId] = useState(presetProjectId || "");
    const [siteId, setSiteId] = useState("");
    const [buildingId, setBuildingId] = useState("");
    const [templateId, setTemplateId] = useState("");
    const [title, setTitle] = useState("");
    const [dueDate, setDueDate] = useState("");
    const [message, setMessage] = useState("");
    const [consultantId, setConsultantId] = useState("");
    const [secureLink, setSecureLink] = useState(false);
    const [recipients, setRecipients] = useState([]);
    const [freeName, setFreeName] = useState("");
    const [freeEmail, setFreeEmail] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get("/clients").then((r) => setClients(r.data)).catch(() => { });
        api.get("/rams/templates", { params: { active: true } }).then((r) => setTemplates(r.data.filter((t) => t.status === "published"))).catch(() => { });
        api.get("/forms/staff").then((r) => setStaff(r.data)).catch(() => { });
    }, []);

    useEffect(() => {
        if (!clientId) { setProjects([]); setSites([]); return; }
        api.get("/projects", { params: { client_id: clientId } }).then((r) => setProjects(r.data)).catch(() => { });
        api.get("/sites", { params: { client_id: clientId } }).then((r) => setSites(r.data)).catch(() => { });
    }, [clientId]);

    useEffect(() => {
        if (!clientId) { setBuildings([]); return; }
        api.get("/buildings", { params: { client_id: clientId, ...(siteId ? { site_id: siteId } : {}) } }).then((r) => setBuildings(r.data)).catch(() => { });
    }, [clientId, siteId]);

    useEffect(() => {
        if (!projectId) { setPeople([]); return; }
        api.get(`/projects/${projectId}/access`).then((r) => setPeople(r.data.client_people || [])).catch(() => setPeople([]));
    }, [projectId]);

    const picked = useMemo(() => new Set(recipients.map((r) => r.contact_id).filter(Boolean)), [recipients]);
    const togglePerson = (p) => {
        setRecipients((rs) => picked.has(p.contact_id)
            ? rs.filter((r) => r.contact_id !== p.contact_id)
            : [...rs, { contact_id: p.contact_id, name: p.name, email: p.email }]);
    };
    const addFree = () => {
        if (!freeName.trim()) return;
        setRecipients((rs) => [...rs, { name: freeName.trim(), email: freeEmail.trim() || undefined }]);
        setFreeName(""); setFreeEmail("");
    };

    const buildPayload = () => ({
        client_id: clientId, project_id: projectId, site_id: siteId || null, building_id: buildingId || null,
        template_id: templateId, title: title || undefined, due_date: dueDate || null, message: message || undefined,
        assigned_consultant_id: consultantId || undefined,
        assigned_consultant_name: staff.find((s) => s.id === consultantId)?.name,
        secure_link_enabled: secureLink, recipients,
    });

    const validate = (requireRecipients) => {
        if (!clientId || !projectId || !templateId) { toast.error("Client, project and template are required"); return false; }
        if (requireRecipients && recipients.length === 0) { toast.error("Add at least one recipient before issuing"); return false; }
        return true;
    };

    const saveDraft = async () => {
        if (!validate(false)) return;
        setSaving(true);
        try {
            const { data } = await api.post("/rams/requests", buildPayload());
            toast.success(`Draft ${data.reference} created`);
            onDone(data);
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not create request"); }
        setSaving(false);
    };

    const issueNow = async () => {
        if (!validate(true)) return;
        setSaving(true);
        try {
            const { data } = await api.post("/rams/requests", buildPayload());
            const { data: issued } = await api.post(`/rams/requests/${data.id}/issue`);
            toast.success(`${issued.reference} issued`);
            onDone(issued);
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not issue request"); }
        setSaving(false);
    };

    // Prompt 3 — direct generation: PR starts a RAMS from scratch (no client request).
    const startDirect = async () => {
        if (!validate(false)) return;
        setSaving(true);
        try {
            const { data } = await api.post("/rams/requests/direct", buildPayload());
            toast.success(`${data.reference} started — fill the details and generate`);
            onDone(data);
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not start RAMS"); }
        setSaving(false);
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" data-testid="rams-issue-dialog">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-xl max-h-[90vh] flex flex-col overflow-hidden">
                <div className="px-5 py-4 border-b border-[#E5E0DA] flex items-center justify-between">
                    <h3 className="text-base font-bold text-[#1F1F1F]">{directMode ? "Generate RAMS directly" : "New RAMS request"}</h3>
                    <button onClick={onClose} className="p-1 text-[#9A968F] hover:text-[#1F1F1F]"><X size={16} /></button>
                </div>
                <div className="p-5 space-y-3 overflow-y-auto flex-1">
                    {directMode && (
                        <div className="flex items-start gap-2 text-[12px] text-[#5A5650] bg-[#F5F3F0] border border-[#E5E0DA] rounded-lg px-3 py-2">
                            <Info size={13} className="mt-0.5 shrink-0" />
                            <span>Start a RAMS from scratch — no client request needed. You'll fill in the project details and generate the AI draft yourself.</span>
                        </div>
                    )}
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Client *">
                            <select data-testid="rams-issue-client" value={clientId} disabled={!!presetClientId}
                                onChange={(e) => { setClientId(e.target.value); setProjectId(""); }} className={inputCls}>
                                <option value="">Select client…</option>
                                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                        </Field>
                        <Field label="Project *">
                            <select data-testid="rams-issue-project" value={projectId} disabled={!!presetProjectId}
                                onChange={(e) => setProjectId(e.target.value)} className={inputCls}>
                                <option value="">Select project…</option>
                                {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                            </select>
                        </Field>
                        <Field label="Site">
                            <select value={siteId} onChange={(e) => setSiteId(e.target.value)} className={inputCls}>
                                <option value="">—</option>
                                {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                            </select>
                        </Field>
                        <Field label="Building">
                            <select value={buildingId} onChange={(e) => setBuildingId(e.target.value)} className={inputCls}>
                                <option value="">—</option>
                                {buildings.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                            </select>
                        </Field>
                    </div>
                    <Field label="Questionnaire template *">
                        <select data-testid="rams-issue-template" value={templateId} onChange={(e) => setTemplateId(e.target.value)} className={inputCls}>
                            <option value="">Select a published template…</option>
                            {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                        </select>
                        {templates.length === 0 && (
                            <p className="text-[11px] text-[#C68A1C] mt-1 flex items-center gap-1"><Info size={11} />No published templates yet — publish one first.</p>
                        )}
                    </Field>
                    <Field label="Title (optional, defaults to template name)">
                        <input value={title} onChange={(e) => setTitle(e.target.value)} className={inputCls} />
                    </Field>
                    <div className="grid grid-cols-2 gap-3">
                        {!directMode && (
                            <Field label="Due date">
                                <input data-testid="rams-issue-due-date" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className={inputCls} />
                            </Field>
                        )}
                        <Field label="Assigned PR consultant">
                            <select value={consultantId} onChange={(e) => setConsultantId(e.target.value)} className={inputCls}>
                                <option value="">Unassigned</option>
                                {staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                            </select>
                        </Field>
                    </div>
                    {!directMode && (
                        <Field label="Message to recipients">
                            <textarea rows={2} value={message} onChange={(e) => setMessage(e.target.value)} className={inputCls + " resize-none"} />
                        </Field>
                    )}
                    {!directMode && (
                        <label className="flex items-center gap-2 text-[13px] text-[#1F1F1F]">
                            <input type="checkbox" checked={secureLink} onChange={(e) => setSecureLink(e.target.checked)} />
                            Also generate a secure external link (for recipients without a portal login)
                        </label>
                    )}

                    {!directMode && (
                    <div>
                        <div className="text-[11px] font-bold uppercase tracking-wider text-[#6F6A64] flex items-center gap-1 mb-1"><Users size={12} /> Recipients</div>
                        {projectId && (
                            <div className="border rounded-lg divide-y max-h-32 overflow-y-auto mb-2" style={{ borderColor: "#E5E0DA" }}>
                                {people.length === 0 ? <div className="text-[12px] italic text-[#9A968F] p-2">No portal contacts on this project — add manually below.</div> :
                                    people.map((p) => (
                                        <label key={p.contact_id} data-testid={`rams-issue-person-${p.contact_id}`} className="flex items-center gap-2 px-2 py-1.5 text-[13px] hover:bg-[#F6F4F0] cursor-pointer">
                                            <input type="checkbox" checked={picked.has(p.contact_id)} onChange={() => togglePerson(p)} />
                                            <span className="font-semibold text-[#1F1F1F]">{p.name}</span>
                                            <span className="text-[#9A968F] text-[11px]">{p.email}</span>
                                        </label>
                                    ))}
                            </div>
                        )}
                        <div className="flex items-center gap-2">
                            <input data-testid="rams-issue-free-name" value={freeName} onChange={(e) => setFreeName(e.target.value)} placeholder="Add name…" className={inputCls + " flex-1"} />
                            <input value={freeEmail} onChange={(e) => setFreeEmail(e.target.value)} placeholder="email" className={inputCls + " flex-1"} />
                            <button data-testid="rams-issue-free-add" onClick={addFree} className="px-3 py-2 rounded-lg text-[12.5px] font-semibold border bg-white hover:bg-[#F6F4F0]" style={{ borderColor: "#E5E0DA" }}>Add</button>
                        </div>
                        {recipients.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mt-2">
                                {recipients.map((r, i) => (
                                    <span key={i} className="text-[11px] px-2 py-1 rounded-full bg-[#FBEFE5] text-[#9F4212] font-semibold flex items-center gap-1">
                                        {r.name}
                                        <button onClick={() => setRecipients((rs) => rs.filter((_, j) => j !== i))}><X size={10} /></button>
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                    )}
                </div>
                <div className="px-5 py-3 border-t border-[#E5E0DA] flex items-center justify-end gap-2 bg-[#F5F3F0]">
                    <button onClick={onClose} className="px-3 py-2 text-sm text-[#6F6A64] hover:text-[#1F1F1F]">Cancel</button>
                    {directMode ? (
                        <button data-testid="rams-direct-start" onClick={startDirect} disabled={saving}
                            className="inline-flex items-center gap-2 text-sm font-semibold px-3.5 py-2 rounded-lg text-white disabled:opacity-60"
                            style={{ background: "var(--brand-accent, #C75A1B)" }}>
                            {saving ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Start & fill details
                        </button>
                    ) : (
                        <>
                            <button data-testid="rams-issue-save-draft" onClick={saveDraft} disabled={saving}
                                className="inline-flex items-center gap-2 text-sm font-semibold px-3.5 py-2 rounded-lg border border-[#E5E0DA] text-[#1F1F1F] hover:bg-white disabled:opacity-60">
                                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save as draft
                            </button>
                            <button data-testid="rams-issue-now" onClick={issueNow} disabled={saving}
                                className="inline-flex items-center gap-2 text-sm font-semibold px-3.5 py-2 rounded-lg text-white disabled:opacity-60"
                                style={{ background: "var(--brand-accent, #C75A1B)" }}>
                                {saving ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Issue now
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return <div><label className="block text-[11px] font-semibold uppercase tracking-wider text-[#6F6A64] mb-1">{label}</label>{children}</div>;
}
